import React from "react";
import styled from "styled-components/macro";
import { Card } from "components/hds";

/**
 * One number with a label, on a card — or several numbers that belong together.
 *
 * Shared rather than owned by a page: the same tile carries the platform counts
 * on the staff-only /internal dashboard and the organization counts on
 * /admin/statistics, and those two must keep looking like one another.
 *
 * Takes strings, not translation keys. /internal is English-only by design and
 * /admin/statistics is translated, so the caller resolves its own text.
 */

export interface NumberTileProps {
	label: string;
	value: number | string;
	/**
	 * Further numbers on the same tile, spread across its width. Two of them
	 * sit left and centre; three or more use the full width, the first left,
	 * the last right, the rest centred between them.
	 *
	 * For figures that belong together and would invite reading as unrelated
	 * totals if split across tiles: a count and the same count over a window,
	 * objects and materials. They are data points of equal standing — same
	 * size, same weight, same ink — and their position is all that tells them
	 * apart, which is why each one needs its own caption in `note`.
	 */
	moreValues?: number[];
	/**
	 * Under the numbers. A single string runs the width of the tile, for a
	 * caveat about the tile as a whole ("Excluding HEGIAS accounts").
	 *
	 * An array captions the numbers one by one — pass one entry per number, in
	 * the same order. Each caption is stacked under the number it names, the
	 * two centred on each other, so the pairing is visible without anything
	 * separating the columns.
	 */
	note?: string | string[];
}

/** Swiss thousands separator — 1 842 rather than 1,842 or 1842. */
export const formatCount = (value: number): string =>
	new Intl.NumberFormat("de-CH").format(value).replace(/’/g, " ");

const NumberTile = ({
	label,
	value,
	moreValues = [],
	note,
}: NumberTileProps) => {
	const values = [value, ...moreValues];
	// A string note describes the whole tile, so it is not a column caption and
	// is rendered under the row instead.
	const captions = Array.isArray(note) ? note : [];

	return (
		<StyledCard>
			<StyledLabel>{label}</StyledLabel>

			<StyledColumns>
				{values.map((entry, index) => (
					// Positional, and the same number can legitimately appear
					// twice — two windows of one collection often read alike —
					// so the index is the only stable identity here. The list
					// is a fixed prop from the call site, never reordered.
					// eslint-disable-next-line react/no-array-index-key
					<StyledColumn key={index}>
						<StyledValue>
							{typeof entry === "number"
								? formatCount(entry)
								: entry}
						</StyledValue>
						{captions.length ? (
							<StyledNote>{captions[index]}</StyledNote>
						) : null}
					</StyledColumn>
				))}
			</StyledColumns>

			{/* Rendered even when there is no note at all, which is what
			    reserves its line: the tiles sit in one grid row and centre
			    their contents, so a tile with two lines would otherwise hang
			    lower than its neighbours with three. */}
			{captions.length ? null : <StyledNote>{note}</StyledNote>}
		</StyledCard>
	);
};

const StyledCard = styled(Card)`
	gap: var(--space-1);
	min-height: 120px;
	justify-content: center;
`;

const StyledLabel = styled.span`
	font-size: var(--font-size-caption);
	font-weight: var(--font-weight-bold);
	color: var(--color-text-secondary);
	text-transform: uppercase;
	letter-spacing: 0.04em;
`;

/**
 * Lays the number/caption stacks out from the left, each as wide as it needs.
 *
 * Content-sized, not equal columns: a caption is as long as its wording makes
 * it, and forcing all of them into equal shares left "Total" adrift in a third
 * of the tile while "Letzte 30 Tage" filled its own. Every stack now takes the
 * width it asks for and the next one starts after a fixed gap, so the row reads
 * as a sentence of figures rather than a table with invisible rules in it.
 *
 * Wraps rather than overflows: three long captions in a 260px column have to go
 * somewhere, and a second line is better than a clipped one.
 */
const StyledColumns = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: var(--space-1) var(--space-5);

	/* A lone number has nothing to sit beside, and centring it over a wide
	   caveat like "Excluding HEGIAS accounts" would leave it floating in the
	   middle of the tile. It keeps the left edge the label already sets. */
	> *:only-child {
		align-items: flex-start;
	}
`;

const StyledColumn = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--space-1);
`;

const StyledValue = styled.span`
	font-size: 34px;
	line-height: 1.1;
	font-weight: var(--font-weight-bold);
	color: var(--color-text);
	font-variant-numeric: tabular-nums;
`;

/* `line-height` and `min-height` are the same number on purpose: one line of
   this text is exactly what an empty note reserves, so every tile in the grid
   is the same height and puts its label and number at the same offset whether
   or not it has a note. Stated rather than inherited, because `normal` line
   height is font-dependent and could not be matched by a min-height. A note
   long enough to wrap still makes its tile taller — the ones here do not. */
const StyledNote = styled.span`
	font-size: var(--font-size-caption);
	line-height: 1.4;
	min-height: 1.4em;
	color: var(--color-text-secondary);
`;

export default NumberTile;
